import { X } from "lucide-react"
import { useCallback, useEffect, useRef, useState } from "react"

interface SymptomSheetProps {
  open: boolean
  onClose: () => void
  onLog: (entry: {
    symptom_type: string
    severity: number
    notes?: string
  }) => void
}

const SYMPTOM_TYPES = [
  "Bloating",
  "Cramping",
  "Nausea",
  "Heartburn",
  "Gas",
  "Diarrhea",
  "Constipation",
  "Acid reflux",
]

export function SymptomSheet({ open, onClose, onLog }: SymptomSheetProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [severity, setSeverity] = useState<number | null>(null)
  const [notes, setNotes] = useState("")

  // Reset form state when the sheet is dismissed so the next open starts fresh
  const resetForm = useCallback(() => {
    setSelectedType(null)
    setSeverity(null)
    setNotes("")
  }, [])

  const handleClose = useCallback(() => {
    resetForm()
    onClose()
  }, [onClose, resetForm])

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) {
        handleClose()
      }
    },
    [handleClose],
  )

  // Escape key closes the sheet
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose()
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [open, handleClose])

  // Focus the close button when sheet opens
  useEffect(() => {
    if (open) {
      closeRef.current?.focus()
    }
  }, [open])

  const handleLog = useCallback(() => {
    if (!selectedType || !severity) return
    const entry = {
      symptom_type: selectedType.toLowerCase(),
      severity,
      notes: notes.trim() || undefined,
    }
    resetForm()
    onLog(entry)
  }, [selectedType, severity, notes, onLog, resetForm])

  const canLog = selectedType !== null && severity !== null

  return (
    <div
      ref={overlayRef}
      className={`symptom-sheet-overlay fixed inset-0 z-30 ${open ? "open" : ""}`}
      style={{ background: "#00000040", overscrollBehavior: "contain" }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="symptom-sheet-title"
    >
      <div
        className="symptom-sheet-panel fixed right-0 bottom-0 left-0 flex flex-col"
        style={{
          background: "var(--fig-action-sheet-bg)",
          borderRadius: "20px 20px 0 0",
          boxShadow: "var(--fig-action-sheet-shadow)",
          maxHeight: "85dvh",
        }}
      >
        {/* Handle + Header */}
        <div className="shrink-0 px-5 pt-3 pb-4">
          <div className="mb-3 flex justify-center">
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: "var(--fig-divider-color)",
              }}
            />
          </div>
          <div className="flex items-center justify-between">
            <h2
              id="symptom-sheet-title"
              style={{ fontSize: 18, fontWeight: 700 }}
            >
              Log Symptom
            </h2>
            <button
              ref={closeRef}
              type="button"
              onClick={handleClose}
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 4,
                color: "var(--fig-text-inactive)",
              }}
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-5 pb-6">
          {/* Symptom type grid */}
          <div style={{ marginBottom: 24 }}>
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--fig-text-inactive)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 10,
              }}
            >
              Type
            </p>
            <div
              role="radiogroup"
              aria-label="Symptom type"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
              }}
            >
              {SYMPTOM_TYPES.map((type) => {
                const isSelected = selectedType === type
                return (
                  <button
                    key={type}
                    type="button"
                    role="radio"
                    aria-checked={isSelected}
                    onClick={() => setSelectedType(type)}
                    className={
                      isSelected
                        ? "symptom-type-btn selected"
                        : "symptom-type-btn"
                    }
                    style={{
                      border: isSelected
                        ? `2px solid var(--fig-entry-symptom-icon)`
                        : `2px solid var(--fig-card-border)`,
                      borderRadius: 12,
                      padding: "10px 12px",
                      fontSize: 14,
                      fontWeight: isSelected ? 600 : 400,
                      color: isSelected
                        ? "var(--fig-entry-symptom-icon)"
                        : "var(--fig-text-primary)",
                      background: isSelected
                        ? "var(--fig-entry-symptom-bg)"
                        : "var(--fig-action-item-hover)",
                      cursor: "pointer",
                      textAlign: "left",
                      transition:
                        "background 120ms ease, border-color 120ms ease, color 120ms ease",
                    }}
                  >
                    {type}
                  </button>
                )
              })}
            </div>
          </div>

          {/* Severity picker */}
          <div style={{ marginBottom: 24 }}>
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--fig-text-inactive)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 10,
              }}
            >
              Severity
            </p>
            <div
              style={{
                display: "flex",
                gap: 8,
                justifyContent: "space-between",
              }}
            >
              {[1, 2, 3, 4, 5].map((level) => {
                const isSelected = severity === level
                return (
                  <button
                    key={level}
                    type="button"
                    aria-label={`Severity ${level}`}
                    aria-pressed={isSelected}
                    onClick={() => setSeverity(level)}
                    className={
                      isSelected
                        ? "symptom-severity-circle selected"
                        : "symptom-severity-circle"
                    }
                    style={{
                      width: 48,
                      height: 48,
                      borderRadius: "50%",
                      border: isSelected
                        ? `2px solid var(--fig-entry-symptom-icon)`
                        : `2px solid var(--fig-card-border)`,
                      background: isSelected
                        ? "var(--fig-entry-symptom-bg)"
                        : "var(--fig-action-item-hover)",
                      color: isSelected
                        ? "var(--fig-entry-symptom-icon)"
                        : "var(--fig-text-primary)",
                      fontSize: 16,
                      fontWeight: isSelected ? 700 : 400,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flex: 1,
                      transition:
                        "background 120ms ease, border-color 120ms ease, color 120ms ease",
                    }}
                  >
                    {level}
                  </button>
                )
              })}
            </div>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                marginTop: 6,
              }}
            >
              <span
                style={{ fontSize: 11, color: "var(--fig-text-placeholder)" }}
              >
                Mild
              </span>
              <span
                style={{ fontSize: 11, color: "var(--fig-text-placeholder)" }}
              >
                Severe
              </span>
            </div>
          </div>

          {/* Notes textarea */}
          <div style={{ marginBottom: 24 }}>
            <p
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: "var(--fig-text-inactive)",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                marginBottom: 10,
              }}
            >
              Notes (optional)
            </p>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add notes (optional)"
              rows={3}
              style={{
                width: "100%",
                background: "var(--fig-action-item-hover)",
                border: `1px solid var(--fig-card-border)`,
                borderRadius: 12,
                padding: "10px 12px",
                fontSize: 14,
                color: "var(--fig-text-primary)",
                resize: "vertical",
                outline: "none",
                fontFamily: "inherit",
                boxSizing: "border-box",
              }}
            />
          </div>

          {/* Log button */}
          <button
            type="button"
            onClick={handleLog}
            disabled={!canLog}
            style={{
              width: "100%",
              padding: "14px 0",
              borderRadius: 14,
              border: "none",
              background: canLog
                ? "var(--fig-fab-bg)"
                : "var(--fig-action-item-hover)",
              color: canLog ? "#ffffff" : "var(--fig-text-placeholder)",
              fontSize: 16,
              fontWeight: 700,
              cursor: canLog ? "pointer" : "not-allowed",
              opacity: canLog ? 1 : 0.6,
              transition: "opacity 150ms ease, background 150ms ease",
              boxShadow: canLog ? "var(--fig-fab-shadow)" : "none",
            }}
          >
            Log
          </button>
        </div>
      </div>
    </div>
  )
}
